import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy,
  Zap,
  Target,
  BarChart3,
  Star,
  Award,
  Users,
  CheckCircle,
  Clock,
  TrendingUp,
  Shield,
  DollarSign,
  Lightbulb,
  ChevronRight,
  Medal,
  Crown,
  Sparkles,
  Brain,
  AlertTriangle,
  Eye,
  ClipboardList,
  Cpu,
  Database,
  GitMerge,
  Code,
  Rocket,
  PackageCheck,
  ShieldCheck,
  FileText
} from 'lucide-react';
import { Top10Idea } from '../../json parser/text_to_json';

interface EvaluationCriteria {
  id: string;
  name: string;
  description: string;
  weight: number;
  icon: React.ElementType;
}

interface IdeaScore {
  ideaId: string;
  scores: Record<string, number>;
  totalScore: number;
  rank: number;
  reasoning: string;
}

interface TournamentMatch {
  id: string;
  idea1: any;
  idea2: any;
  winner: string | null;
  criteria: string;
  round: number;
}

interface IdeaEvaluationPhaseProps {
  ideas: any[];
  onComplete: (results: any) => void;
  initialState?: {
    selectedIdeas?: any[];
    evaluationComplete?: boolean;
    evaluationLogs?: string[];
    currentStage?: number;
    stageProgress?: number;
    ideaScores?: IdeaScore[];
    evaluationCriteria?: EvaluationCriteria[];
  };
  onStateChange?: (state: any) => void;
  top10Ideas?: Top10Idea[];
  marketResearch?: {
    marketSize?: string;
    competitorAnalysis?: string;
    targetSegment?: string;
    revenueModel?: string;
    timeToMarket?: string;
    riskFactors?: string[];
  };
  keyValuePoints?: string[];
  businessCase?: string;
  parsingError?: string | null;
  isCompleted?: boolean;
  phaseResults?: any;
}

// Hardcoded Top 10 Innovation Ideas Data
const TOP_10_INNOVATION_IDEAS = [
  {
    id: '561',
    title: 'Homomorphic Machine Learning Platform',
    description: 'ML platform that trains and executes models on encrypted data without ever decrypting sensitive information.',
    category: 'AI Enhancement',
    score: {
      ideaId: '561',
      scores: { innovation: 9.8, feasibility: 8.5, impact: 9.7, cost: 8.9, security: 9.9 },
      totalScore: 9.3,
      rank: 1,
      reasoning: 'Excels in Innovation and Security with revolutionary privacy-preserving capabilities'
    },
    difficulty: 'Very High',
    timeToImplement: '12-18 months',
    impact: 'Revolutionary',
    tags: ['Privacy', 'AI', 'Encryption', 'Machine Learning'],
    trizPrinciples: [15, 2, 40], // Dynamics, Taking out, Composite materials
    contradiction: 'Improving ML model accuracy vs. preserving complete data privacy',
    keyStrengths: ['Privacy-preserving AI', 'Breakthrough technology', 'Market differentiation'],
    areasForImprovement: ['Computational overhead', 'Implementation complexity'],
    detailedData: {
      whyThisIsNumberOne: {
        title: 'Why This Is The #1 Privacy Innovation',
        points: [
          "**Breakthrough Technology Convergence:** Combines homomorphic encryption with machine learning to achieve what was previously impossible.",
          "**Massive Market Potential:** Unlocks AI in regulated industries like healthcare and finance, a $100B+ opportunity.",
          "**Solves Critical Industry Pain Points:** Enables HIPAA-compliant healthcare AI and fraud detection without data exposure."
        ]
      },
      revolutionaryFeatures: {
        core: [
          "**Encrypted Model Training:** Train neural networks on fully encrypted datasets without decryption",
          "**Secure Inference Engine:** Make predictions on encrypted queries with zero-knowledge processing",
          "**Federated Homomorphic Learning:** Multiple parties contribute encrypted data for collaborative training"
        ],
        advanced: [
          "Gradient Computation on Encrypted Data",
          "Secure Multi-Party Model Updates", 
          "Privacy-Preserving Model Evaluation",
          "Encrypted Feature Engineering"
        ]
      },
      futurePotential: {
        fiveYear: [
          "**Healthcare Revolution:** Global disease prediction models trained on encrypted patient data",
          "**Financial Transformation:** Global fraud detection without exposing transactions",
          "**Enterprise AI Democratization:** Small companies access AI trained on industry data"
        ],
        tenYear: [
          "**Regulatory Compliance:** Becomes the standard for AI in regulated industries",
          "**Global Collaboration:** International AI research without data sharing concerns",
          "**Democratic AI:** Individual privacy preserved while benefiting from collective intelligence"
        ]
      },
      commercialValue: {
        revenueStreams: [
          "Platform Licensing: $10M-100M annually per enterprise",
          "API Usage: Pay-per-encrypted-computation model",
          "Industry Solutions: Vertical-specific implementations"
        ],
        costSavings: [
          "Reduce compliance and regulatory costs by 60-80%",
          "Eliminate data breach exposure risks",
          "Faster deployment with no data anonymization delays"
        ],
        competitiveAdvantages: [
          "First-mover advantage with revolutionary technology",
          "Network effects as more participants increase model accuracy",
          "Regulatory alignment solving future privacy requirements today"
        ]
      },
      innovationHighlights: {
        technical: [
          "Novel algorithms reducing homomorphic computation overhead by 40%",
          "Parallel processing architectures for encrypted ML operations",
          "Optimized circuit designs for common ML models"
        ],
        industry: [
          "First commercially viable homomorphic ML implementation",
          "Enables cross-border AI training without data movement",
          "Built-in compliance with global privacy laws (GDPR, CCPA)"
        ]
      },
      comparisonToOthers: {
        vsQuantum: "**Immediate Market Need:** Privacy is urgent today, quantum threats are future-focused",
        vsZeroKnowledge: "**Transformative Impact:** Changes entire AI industry vs. verification use cases",
        vsFederated: "**Stronger Privacy:** True encryption vs. federated aggregation"
      },
      implementationRoadmap: [
        "Phase 1 (Months 1-6): Foundation - Core homomorphic encryption engine and basic ML operations",
        "Phase 2 (Months 7-12): Advanced ML - Deep learning support and real-world pilot with healthcare partner",
        "Phase 3 (Months 13-18): Platform - Full-featured ML platform, API ecosystem, and commercial launch"
      ],
      technicalDeepDive: {
        architecture: "A microservices architecture with homomorphic encryption at the core, supporting distributed computation across encrypted data sources.",
        techStack: {
          frontend: "React/TypeScript with privacy-focused UI components",
          backend: "Rust for performance-critical homomorphic operations",
          database: "Encrypted data stores with homomorphic query capabilities",
          infra: "Kubernetes with secure enclaves on AWS/GCP"
        },
        integrationPoints: [
          "Healthcare systems (EPIC, Cerner) with HIPAA compliance",
          "Financial platforms with PCI DSS integration",
          "Government systems with classified data handling"
        ]
      },
      resourceAllocation: {
        team: "1 Product Manager, 3 Senior Cryptography Engineers, 2 ML Engineers, 1 Frontend Engineer, 1 DevOps/SRE",
        budgetNote: "Initial budget should cover 18 months of runway for specialized team plus significant R&D costs"
      },
      trizFlow: {
        contradiction: "Improving ML model accuracy vs. preserving complete data privacy",
        principle: "Leveraging TRIZ Principles 15 (Dynamics), 2 (Taking out), 40 (Composite materials) to enable computation on encrypted data",
        implementation: "Develop homomorphic encryption algorithms that allow ML training without decryption, using dynamic key management",
        benefit: "Achieves full data privacy while maintaining ML accuracy and enabling new AI applications in regulated industries"
      },
      marketResearch: {
        marketSize: "$50B+ market creation within 10 years",
        competitorAnalysis: "No direct competitors for production-ready homomorphic ML; academic research stage globally",
        targetSegment: "Healthcare, Finance, Government, and Enterprise AI requiring privacy compliance",
        revenueModel: "Tiered SaaS with usage-based pricing for encrypted computations",
        timeToMarket: "12-18 months for MVP, 24 months for full platform",
        riskFactors: [
          "Computational overhead challenges for large-scale deployment",
          "Regulatory acceptance and standardization timeline",
          "Competition from tech giants entering the space"
        ]
      },
      keyPoints: [
        "Addresses the holy grail of privacy-preserving AI with $50B+ market potential",
        "First-mover advantage in commercially viable homomorphic ML",
        "Solves critical privacy-utility tradeoff that blocks AI adoption",
        "Enables entire new categories of AI applications in regulated industries"
      ],
      detailedAnalysis: "This idea ranks #1 due to its revolutionary approach to the privacy-utility tradeoff in AI. By enabling machine learning on encrypted data, it unlocks massive markets in healthcare, finance, and government that are currently blocked by privacy constraints. The technical innovation represents a genuine breakthrough, while the commercial potential is unprecedented."
    }
  },
  {
    id: '562',
    title: 'Quantum-Safe Blockchain Infrastructure',
    description: 'Next-generation blockchain with quantum-resistant cryptography and post-quantum security protocols.',
    category: 'Security & Compliance',
    score: {
      ideaId: '562',
      scores: { innovation: 9.2, feasibility: 8.8, impact: 9.5, cost: 8.4, security: 9.9 },
      totalScore: 9.1,
      rank: 2,
      reasoning: 'Excels in Security and Impact with future-proof cryptographic foundation'
    },
    difficulty: 'Very High',
    timeToImplement: '15-20 months',
    impact: 'Transformative',
    tags: ['Quantum', 'Blockchain', 'Cryptography', 'Security'],
    keyStrengths: ['Quantum-resistant security', 'Future-proof technology', 'Enterprise adoption'],
    areasForImprovement: ['Technical complexity', 'Market readiness']
  },
  {
    id: '563',
    title: 'Zero-Knowledge Identity Verification',
    description: 'Identity verification system that proves identity without revealing any personal information.',
    category: 'Security & Compliance',
    score: {
      ideaId: '563',
      scores: { innovation: 9.4, feasibility: 8.2, impact: 9.0, cost: 8.8, security: 9.8 },
      totalScore: 9.0,
      rank: 3,
      reasoning: 'Excels in Innovation and Security with breakthrough privacy technology'
    },
    difficulty: 'High',
    timeToImplement: '10-14 months',
    impact: 'Revolutionary',
    tags: ['Zero-Knowledge', 'Identity', 'Privacy', 'Verification'],
    keyStrengths: ['Privacy preservation', 'Regulatory compliance', 'User trust'],
    areasForImprovement: ['User adoption', 'Integration complexity']
  },
  {
    id: '564',
    title: 'Autonomous Code Generation Engine',
    description: 'AI system that generates, tests, and deploys production-ready code from natural language requirements.',
    category: 'AI Enhancement',
    score: {
      ideaId: '564',
      scores: { innovation: 8.9, feasibility: 8.5, impact: 9.2, cost: 9.1, security: 8.5 },
      totalScore: 8.8,
      rank: 4,
      reasoning: 'Excels in Impact and Cost with transformative development capabilities'
    },
    difficulty: 'High',
    timeToImplement: '8-12 months',
    impact: 'Transformative',
    tags: ['AI', 'Code Generation', 'Automation', 'Development'],
    keyStrengths: ['Development acceleration', 'Cost reduction', 'Code quality'],
    areasForImprovement: ['AI accuracy', 'Edge case handling']
  },
  {
    id: '565',
    title: 'Decentralized Federated Learning Network',
    description: 'Global network for collaborative AI training without centralized data aggregation.',
    category: 'Core Technology',
    score: {
      ideaId: '565',
      scores: { innovation: 8.7, feasibility: 8.9, impact: 8.8, cost: 8.9, security: 8.8 },
      totalScore: 8.8,
      rank: 5,
      reasoning: 'Excels in Feasibility and Cost with strong collaborative AI foundation'
    },
    difficulty: 'High',
    timeToImplement: '12-16 months',
    impact: 'Significant',
    tags: ['Federated Learning', 'Decentralized', 'AI', 'Collaboration'],
    keyStrengths: ['Collaborative AI', 'Privacy preservation', 'Network scalability'],
    areasForImprovement: ['Coordination complexity', 'Data quality control']
  },
  {
    id: '566',
    title: 'Real-Time Threat Intelligence Mesh',
    description: 'Distributed security intelligence network that shares threat data in real-time across organizations.',
    category: 'Security & Compliance',
    score: {
      ideaId: '566',
      scores: { innovation: 8.5, feasibility: 9.0, impact: 8.9, cost: 8.7, security: 9.2 },
      totalScore: 8.7,
      rank: 6,
      reasoning: 'Excels in Feasibility and Security with strong threat prevention capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '8-12 months',
    impact: 'Significant',
    tags: ['Threat Intelligence', 'Security', 'Real-time', 'Collaboration'],
    keyStrengths: ['Real-time detection', 'Collaborative defense', 'Threat prevention'],
    areasForImprovement: ['Data sharing trust', 'False positive rates']
  },
  {
    id: '567',
    title: 'Edge AI Optimization Platform',
    description: 'Platform for deploying and optimizing AI models on edge devices with minimal resource consumption.',
    category: 'Core Technology',
    score: {
      ideaId: '567',
      scores: { innovation: 8.3, feasibility: 9.1, impact: 8.7, cost: 9.0, security: 8.4 },
      totalScore: 8.7,
      rank: 7,
      reasoning: 'Excels in Feasibility and Cost with strong edge computing capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '6-10 months',
    impact: 'Significant',
    tags: ['Edge Computing', 'AI', 'Optimization', 'IoT'],
    keyStrengths: ['Resource efficiency', 'Low latency', 'Edge deployment'],
    areasForImprovement: ['Hardware diversity', 'Model compression']
  },
  {
    id: '568',
    title: 'Adaptive Security Architecture',
    description: 'Self-healing security system that adapts to new threats and automatically implements countermeasures.',
    category: 'Security & Compliance',
    score: {
      ideaId: '568',
      scores: { innovation: 8.6, feasibility: 8.4, impact: 8.8, cost: 8.5, security: 9.0 },
      totalScore: 8.6,
      rank: 8,
      reasoning: 'Excels in Innovation and Security with adaptive threat response'
    },
    difficulty: 'High',
    timeToImplement: '10-14 months',
    impact: 'Significant',
    tags: ['Adaptive Security', 'Self-healing', 'Threat Response', 'Automation'],
    keyStrengths: ['Automated response', 'Self-healing capability', 'Threat adaptation'],
    areasForImprovement: ['False positive handling', 'Response accuracy']
  },
  {
    id: '569',
    title: 'Semantic Data Mesh Platform',
    description: 'Next-generation data architecture that provides semantic understanding and automatic data governance.',
    category: 'Core Technology',
    score: {
      ideaId: '569',
      scores: { innovation: 8.2, feasibility: 8.8, impact: 8.6, cost: 8.8, security: 8.5 },
      totalScore: 8.6,
      rank: 9,
      reasoning: 'Excels in Feasibility and Cost with strong data management capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '8-12 months',
    impact: 'Significant',
    tags: ['Data Mesh', 'Semantic', 'Governance', 'Architecture'],
    keyStrengths: ['Data governance', 'Semantic understanding', 'Mesh architecture'],
    areasForImprovement: ['Data quality', 'Semantic accuracy']
  },
  {
    id: '570',
    title: 'Autonomous Infrastructure Scaling',
    description: 'AI-driven infrastructure that automatically scales, optimizes, and heals itself based on usage patterns.',
    category: 'Core Technology',
    score: {
      ideaId: '570',
      scores: { innovation: 8.1, feasibility: 8.9, impact: 8.5, cost: 9.0, security: 8.2 },
      totalScore: 8.5,
      rank: 10,
      reasoning: 'Excels in Feasibility and Cost with strong automation capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '6-9 months',
    impact: 'Significant',
    tags: ['Infrastructure', 'Automation', 'Scaling', 'Self-healing'],
    keyStrengths: ['Automated scaling', 'Cost optimization', 'Self-healing'],
    areasForImprovement: ['Resource prediction', 'Scaling accuracy']
  }
];

const IdeaEvaluationPhase: React.FC<IdeaEvaluationPhaseProps> = ({
  ideas,
  onComplete,
  initialState,
  onStateChange,
  top10Ideas,
  marketResearch,
  keyValuePoints,
  businessCase,
  parsingError,
  isCompleted,
  phaseResults
}) => {
  const [currentStage, setCurrentStage] = useState(initialState?.currentStage || 0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [evaluationLogs, setEvaluationLogs] = useState<string[]>(initialState?.evaluationLogs || []);
  const [evaluationCriteria, setEvaluationCriteria] = useState<EvaluationCriteria[]>(initialState?.evaluationCriteria || []);
  const [ideaScores, setIdeaScores] = useState<IdeaScore[]>(initialState?.ideaScores || []);
  const [tournamentMatches, setTournamentMatches] = useState<TournamentMatch[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<any>(null);
  const [currentMatch, setCurrentMatch] = useState<TournamentMatch | null>(null);
  const [stageProgress, setStageProgress] = useState(initialState?.stageProgress || 0);
  const [isEvaluationCompleted, setIsEvaluationCompleted] = useState(initialState?.evaluationComplete || false);
  const [gameRules, setGameRules] = useState<any>(null);
  const [scoringMatrix, setScoringMatrix] = useState<any[]>([]);
  const [idealityScores, setIdealityScores] = useState<any[]>([]);
  const [currentProcessingStep, setCurrentProcessingStep] = useState<string>('');
  const [tournamentBrackets, setTournamentBrackets] = useState<any[]>([]);
  const [liveScoring, setLiveScoring] = useState<any[]>([]);
  
  const logsRef = useRef<HTMLDivElement>(null);
  
  // Add refs to track if we've already logged certain things
  const hasLoggedPropsRef = useRef(false);
  const hasLoggedIdeasRef = useRef(false);
  const lastTop10IdeasCountRef = useRef<number | undefined>(undefined);

  // Memoize the conversion function to prevent recreating it on every render
  const convertTop10ToExpectedFormat = React.useCallback((top10Ideas: Top10Idea[]) => {
    // Only log the summary once, not for each conversion
    if (process.env.NODE_ENV === 'development') {
      console.log('IdeaEvaluationPhase - convertTop10ToExpectedFormat - Converting ideas:', {
        inputCount: top10Ideas.length,
        inputRanks: top10Ideas.map(idea => idea.rank).sort((a, b) => a - b)
      });
    }
    
    return top10Ideas.map((idea, index) => {
      const sanitizeAndNormalizeScore = (score: any, isCost: boolean = false): number => {
        let numericScore = 0;
        if (typeof score === 'string') {
          const match = score.match(/^[\d.]+/);
          numericScore = match ? parseFloat(match[0]) : 0;
        } else if (typeof score === 'number') {
          numericScore = score;
        }

        if (isNaN(numericScore)) return isCost ? 5 : 5; // Return a neutral default

        // Scores are already in 1-10 scale from the JSON, no need to normalize
        return numericScore;
      };
      
      const individualScoresArray = idea.individual_scores || [];
      const innovationJustification = individualScoresArray.find(s => s.criterion === 'Innovation Potential')?.justification;

      const individualScores: Record<string, number> = individualScoresArray.reduce((acc, scoreItem) => {
          acc[scoreItem.criterion] = scoreItem.score;
          return acc;
      }, {} as Record<string, number>);

      const scores = {
        innovation: sanitizeAndNormalizeScore(individualScores['Innovation Potential']) || 8.0,
        feasibility: sanitizeAndNormalizeScore(individualScores['Technical Feasibility']) || 7.5,
        impact: sanitizeAndNormalizeScore(individualScores['Market Impact']) || 8.0,
        security: sanitizeAndNormalizeScore(individualScores['Risk Assessment']) || 7.5,
        cost: sanitizeAndNormalizeScore(individualScores['Financial Viability']) || 7.5,
        implementation: sanitizeAndNormalizeScore(individualScores['Implementation Viability']) || 7.5,
        strategic: sanitizeAndNormalizeScore(individualScores['Strategic Alignment']) || 8.0,
        competitive: sanitizeAndNormalizeScore(individualScores['Competitive Advantage']) || 7.5,
        sustainability: sanitizeAndNormalizeScore(individualScores['Sustainability Impact']) || 7.0
      };
      
      const totalScore = idea.overall_score || 
        Object.values(scores).reduce((sum, val) => sum + (val || 0), 0) / Object.keys(scores).length;

      const convertedIdea = {
        id: `top10-${index}`,
        title: idea.name,
        description: idea.description,
        category: 'Top 10 Innovation',
        score: {
          ideaId: `top10-${index}`,
          scores: { ...scores },
          totalScore: totalScore,
          rank: idea.rank,
          reasoning: `Excels in ${(idea.key_strengths || []).join(' and ') || 'various aspects'} with ${(idea.areas_for_improvement || []).join(' and ') || 'some areas'} as areas for improvement`
        },
        difficulty: idea.implementation_complexity || (totalScore > 9 ? 'High' : totalScore > 7 ? 'Medium' : 'Low'),
        timeToImplement: idea.financial_analysis?.break_even_timeline || marketResearch?.timeToMarket || '6-12 months',
        impact: totalScore > 9 ? 'Revolutionary' : totalScore > 7 ? 'Significant' : 'Medium',
        tags: (idea.key_strengths || []).slice(0, 3),
        keyStrengths: idea.key_strengths || [],
        areasForImprovement: idea.areas_for_improvement || [],
        originalSource: idea.original_source,
        implementationComplexity: idea.implementation_complexity,
        innovationLevel: idea.innovation_level,
        riskAssessment: idea.risk_assessment,
        qualityThresholdCheck: idea.quality_threshold_check,
        financialAnalysis: idea.financial_analysis,
        marketAnalysis: idea.market_analysis,
        individualScores: idea.individual_scores
      };
      
      // Remove per-item logging to prevent console spam
      
      return convertedIdea;
    });
  }, [marketResearch?.timeToMarket]);

  // Use parsed data if available, otherwise fall back to hardcoded data
  const innovationIdeas = React.useMemo(() => {
    return (top10Ideas && Array.isArray(top10Ideas) && top10Ideas.length > 0) 
      ? convertTop10ToExpectedFormat(top10Ideas)
      : TOP_10_INNOVATION_IDEAS;
  }, [top10Ideas, convertTop10ToExpectedFormat]);

  // Auto-scroll to bottom when logs update
  useEffect(() => {
    if (logsRef.current) {
      logsRef.current.scrollTo({
        top: logsRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [evaluationLogs]);

  // If the phase is already completed, show the completed state immediately
  useEffect(() => {
    if (isCompleted && phaseResults) {
      setIsEvaluationCompleted(true);
      setCurrentStage(4); // Last stage
      setStageProgress(100);
      
      // Restore evaluation logs and criteria if available
      if (phaseResults.evaluationLogs) {
        setEvaluationLogs(phaseResults.evaluationLogs);
      }
      if (phaseResults.evaluationCriteria) {
        setEvaluationCriteria(phaseResults.evaluationCriteria);
      }
      if (phaseResults.ideaScores) {
        setIdeaScores(phaseResults.ideaScores);
      }
    }
  }, [isCompleted, phaseResults]);

  // Effect to notify about state changes
  useEffect(() => {
    if (onStateChange) {
      // Use a timeout to debounce rapid state changes
      const timeoutId = setTimeout(() => {
        onStateChange({
          evaluationComplete: isEvaluationCompleted,
          evaluationLogs,
          currentStage,
          stageProgress,
          ideaScores,
          evaluationCriteria
        });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isEvaluationCompleted, evaluationLogs, currentStage, stageProgress, ideaScores, evaluationCriteria, onStateChange]);

  // Debug logging - only log once when component mounts or when top10Ideas changes significantly
  useEffect(() => {
    // Only log if top10Ideas actually exists and has changed
    if (top10Ideas !== undefined && lastTop10IdeasCountRef.current !== top10Ideas?.length) {
      lastTop10IdeasCountRef.current = top10Ideas?.length;
      
      console.log('IdeaEvaluationPhase - Received props:', {
        top10Ideas,
        top10IdeasType: typeof top10Ideas,
        top10IdeasIsArray: Array.isArray(top10Ideas),
        top10IdeasLength: top10Ideas?.length,
        top10IdeasDetails: Array.isArray(top10Ideas) ? top10Ideas.map(idea => ({
          name: idea.name,
          rank: idea.rank,
          overall_score: idea.overall_score,
          individual_scores: idea.individual_scores,
          key_strengths: idea.key_strengths,
          areas_for_improvement: idea.areas_for_improvement
        })) : 'NOT AN ARRAY',
        marketResearch,
        keyValuePoints,
        businessCase,
        parsingError
      });

      if (!top10Ideas || !Array.isArray(top10Ideas) || top10Ideas.length === 0) {
        console.error(`IdeaEvaluationPhase - Error: Parsed top 10 ideas are missing or empty. Falling back to hardcoded data. Reason: ${parsingError || 'Parsing status unknown. This may be due to a parsing failure in the previous step.'}`);
        console.error('IdeaEvaluationPhase - top10Ideas details:', {
          top10Ideas,
          isUndefined: top10Ideas === undefined,
          isNull: top10Ideas === null,
          isArray: Array.isArray(top10Ideas),
          type: typeof top10Ideas,
          length: top10Ideas?.length
        });
      } else {
        console.log('IdeaEvaluationPhase - Successfully received parsed top 10 ideas:', {
          count: top10Ideas.length,
          ranks: top10Ideas.map(idea => idea.rank).sort((a, b) => a - b),
          allRanksPresent: top10Ideas.map(idea => idea.rank).sort((a, b) => a - b).join(',') === '1,2,3,4,5,6,7,8,9,10',
          missingRanks: Array.from({length: 10}, (_, i) => i + 1).filter(rank => !top10Ideas.some(idea => idea.rank === rank))
        });
      }
      hasLoggedPropsRef.current = true;
    }
  }, [top10Ideas?.length]); // Only re-run when the length changes, not on every render

  // Log ideas usage only when the source changes
  useEffect(() => {
    const source = top10Ideas && top10Ideas.length > 0 ? 'parsed' : 'hardcoded';
    const ideasKey = `${source}-${innovationIdeas.length}`;
    
    // Only log if this is a new combination of source and length
    if (!hasLoggedIdeasRef.current || (window as any).lastIdeasKey !== ideasKey) {
      (window as any).lastIdeasKey = ideasKey;
      console.log('IdeaEvaluationPhase - Using ideas:', {
        source,
        count: innovationIdeas.length,
        firstIdea: innovationIdeas[0],
        firstIdeaScore: innovationIdeas[0]?.score,
        allIdeasScores: innovationIdeas.map(idea => ({
          id: idea.id,
          title: idea.title,
          totalScore: idea.score?.totalScore,
          individualScores: idea.score?.scores,
          rank: idea.score?.rank
        }))
      });
      hasLoggedIdeasRef.current = true;
    }
  }, [innovationIdeas.length, top10Ideas?.length]); // Only log when the number of ideas changes

  const stages = [
    { name: 'Game Rules Creation', duration: 3000, progress: 20 },
    { name: 'Tournament Setup', duration: 2000, progress: 40 },
    { name: 'Multi-parameter Scoring', duration: 4000, progress: 60 },
    { name: 'Tournament Competition', duration: 5000, progress: 80 },
    { name: 'Final Ranking & Selection', duration: 2000, progress: 100 }
  ];

  const criteria: EvaluationCriteria[] = [
    {
      id: 'innovation',
      name: 'Innovation Potential',
      description: 'Overall assessment of novelty, breakthrough potential, IP opportunities, and differentiation from existing solutions',
      weight: 0.18,
      icon: Lightbulb
    },
    {
      id: 'feasibility',
      name: 'Technical Feasibility',
      description: 'Overall assessment of technology readiness, implementation complexity, resource requirements, and scalability',
      weight: 0.16,
      icon: Target
    },
    {
      id: 'impact',
      name: 'Market Impact',
      description: 'Overall assessment of market size, customer value, competitive advantage, and revenue potential',
      weight: 0.16,
      icon: TrendingUp
    },
    {
      id: 'cost',
      name: 'Financial Viability',
      description: 'Overall assessment of ROI potential, payback period, cost-benefit analysis, and financial sustainability',
      weight: 0.14,
      icon: DollarSign
    },
    {
      id: 'implementation',
      name: 'Implementation Viability',
      description: 'Overall assessment of time to market, cost effectiveness, organizational readiness, and regulatory compliance',
      weight: 0.12,
      icon: Rocket
    },
    {
      id: 'strategic',
      name: 'Strategic Alignment',
      description: 'Overall assessment of goal alignment, core competency leverage, vision fit, and portfolio balance',
      weight: 0.10,
      icon: Target
    },
    {
      id: 'security',
      name: 'Risk Assessment',
      description: 'Overall assessment of technical, market, and financial risks and mitigation feasibility',
      weight: 0.08,
      icon: Shield
    },
    {
      id: 'competitive',
      name: 'Competitive Advantage',
      description: 'Overall assessment of sustainable competitive positioning, barriers to entry, and market differentiation',
      weight: 0.08,
      icon: Award
    },
    {
      id: 'sustainability',
      name: 'Sustainability Impact',
      description: 'Overall assessment of environmental, social, economic sustainability and ESG compliance',
      weight: 0.08,
      icon: Sparkles
    }
  ];

  const dataTemplates: any = {
    'AI Enhancement': {
      whyPoints: [
        "**Unlocks New AI Capabilities:** Goes beyond standard models to enable more accurate predictions and automation.",
        "**Data-Driven Decision Making:** Provides deeper insights from existing data, driving significant business value.",
        "**Ethical AI Framework:** Built with fairness and transparency in mind, reducing model bias."
      ],
      features: {
        core: ["**Advanced Model Architecture:** Utilizes a novel neural network structure for superior performance.", "**Real-time Inference Engine:** Capable of processing thousands of requests per second with low latency.", "**Explainability Module (XAI):** Provides clear reasoning for its predictions, building user trust."],
        advanced: ["Self-tuning Hyperparameters", "Federated Learning Support", "Adversarial Attack Resistance"]
      },
      futureVision: "Become the industry standard for trustworthy and high-performance AI, enabling applications previously impossible due to ethical or performance constraints.",
      revenueStreams: ["Tiered SaaS for API access", "Per-prediction enterprise billing", "Consulting for custom model training"],
      comparison: {
        vsQuantum: `**More Immediate Market:** Solves today's AI/ML problems, while quantum is a longer-term play.`,
        vsZeroKnowledge: `**Broader Application:** Applicable to any ML problem, not just verification.`,
        vsFederated: `**Centralized Excellence:** Can achieve higher accuracy with a global model view, assuming data access is possible.`
      }
    },
    'Core Technology': {
       whyPoints: [
        "**Fundamental Infrastructure Improvement:** Re-engineers a core part of the tech stack for 10x performance gains.",
        "**Massive Scalability:** Designed to handle petabyte-scale data and millions of concurrent users.",
        "**Developer Ecosystem Enablement:** Opens up new possibilities for other teams to build upon this platform."
      ],
      features: {
        core: ["**High-Performance Rust/Go Core:** Written in a modern, memory-safe language for maximum speed.", "**Cloud-Native Architecture:** Leverages Kubernetes, serverless, and service mesh for resilience.", "**Extensible Plugin System:** Allows for custom functionality to be added easily."],
        advanced: ["Multi-region Disaster Recovery", "Zero-downtime Deployments", "Advanced Telemetry & Observability"]
      },
      futureVision: "To become the underlying infrastructure for a new generation of high-performance applications, setting the standard for reliability and speed.",
      revenueStreams: ["Open-core model with enterprise licensing", "Managed cloud service offerings", "Professional support and services"],
       comparison: {
        vsQuantum: `**Wider Applicability:** Improves performance across the board, not just for security.`,
        vsZeroKnowledge: `**Platform Play:** Creates a foundation for others, rather than being a single-use-case product.`,
        vsFederated: `**Solves a Different Problem:** Focuses on raw performance and scalability, which can complement federated learning.`
      }
    },
    'Security & Compliance': {
      whyPoints: [
          "**Proactive Threat Mitigation:** Moves from a reactive to a predictive security posture.",
          "**Next-Generation Cryptography:** Implements cutting-edge encryption to protect against future threats.",
          "**Automated Compliance:** Drastically reduces the manual effort required to meet regulatory standards like GDPR and CCPA."
      ],
      features: {
        core: ["**Zero-Trust Policy Engine:** Enforces strict access controls based on dynamic context, not just network location.", "**Confidential Computing Enclave:** Processes sensitive data in a hardware-isolated secure environment.", "**Behavioral Anomaly Detection:** Uses ML to identify suspicious activity that signature-based systems miss."],
        advanced: ["Post-Quantum Cryptography Readiness", "Automated Threat Hunting", "Decentralized Identity Integration"]
      },
      futureVision: "To eliminate entire classes of security vulnerabilities and make robust, automated compliance accessible to all organizations.",
      revenueStreams: ["Per-user security subscription", "Compliance-as-a-service API", "Breach insurance partnerships"],
       comparison: {
        vsQuantum: `**Complementary:** This is the application layer that would run on quantum-safe infrastructure.`,
        vsZeroKnowledge: `**More Comprehensive:** Provides a full security suite, of which ZK could be one feature.`,
        vsFederated: `**Security-First:** Designed with a security threat model from the ground up.`
      }
    },
     'Default': {
      whyPoints: [
        "**Significant Market Opportunity:** Addresses a well-understood and growing market need.",
        "**Strong Competitive Differentiator:** Offers a unique feature set that is difficult for competitors to replicate.",
        "**Clear Path to Monetization:** Has a straightforward business model with proven demand."
      ],
      features: {
        core: ["**Intuitive User Interface:** Simplifies a complex workflow, making it accessible to non-experts.", "**Robust API Layer:** Allows for easy integration into existing customer ecosystems.", "**Advanced Analytics Dashboard:** Provides actionable insights and reporting."],
        advanced: ["Workflow Automation", "Third-party Integrations Marketplace", "AI-powered Recommendations"]
      },
      futureVision: "To become the dominant platform in its niche by providing the best user experience and most powerful feature set.",
      revenueStreams: ["Standard tiered SaaS model", "Usage-based billing", "Professional services for enterprise onboarding"],
       comparison: {
        vsQuantum: `**Lower Technical Risk:** Based on proven technologies, leading to a faster time-to-market.`,
        vsZeroKnowledge: `**Broader Target Audience:** Appeals to a wider user base than a specialized cryptographic tool.`,
        vsFederated: `**Simpler Implementation:** Does not require the complex coordination of multiple data parties.`
      }
    }
  };

  const generateDetailedIdeaData = (idea: any, rank: number, scoreData: any) => {
    // Check if this is the champion idea with detailed data
    if (idea.detailedData) {
      return {
        ...idea,
        score: scoreData,
        ...idea.detailedData
      };
    }

    // Use parsed data if available
    const financialAnalysis = idea.financialAnalysis;
    const marketAnalysis = idea.marketAnalysis;
    const individualScores = idea.individualScores || [];
    
    // Generate template fallback data only if parsed data is not available
    const score = scoreData.totalScore;
    const marketSize = financialAnalysis?.investment_required || marketAnalysis?.target_market_size || (score * 2 + Math.random() * 5).toFixed(1);
    const timeToMarket = marketAnalysis?.target_market_size || idea.timeToImplement || `${Math.floor(score * 1.5 + Math.random() * 6)} - ${Math.floor(score * 2 + Math.random() * 12)} months`;

    const template = dataTemplates[idea.category] || dataTemplates['Default'];

    return {
      ...idea,
      score: scoreData,
      whyThisIsNumberOne: {
        title: `Why This Is The #${rank} Innovation`,
        points: idea.keyStrengths && idea.keyStrengths.length > 0 ? 
          idea.keyStrengths.slice(0, 3).map((strength: string) => `**${strength}:** ${
            individualScores.find((s: any) => s.criterion.toLowerCase().includes(strength.toLowerCase()))?.justification || 
            `This innovation excels in ${strength.toLowerCase()}, providing significant advantages over existing solutions.`
          }`) : 
          template.whyPoints.map((p: string) => p.replace('$', `$${marketSize}`))
      },
      revolutionaryFeatures: {
        core: idea.keyStrengths && idea.keyStrengths.length > 0 ?
          idea.keyStrengths.map((strength: string) => `**${strength}:** ${
            individualScores.find((s: any) => s.justification?.toLowerCase().includes(strength.toLowerCase()))?.justification ||
            `Delivers exceptional ${strength.toLowerCase()} capabilities.`
          }`) :
          template.features.core,
        advanced: idea.areasForImprovement && idea.areasForImprovement.length > 0 ?
          idea.areasForImprovement.map((area: string) => `${area} Enhancement`) :
          template.features.advanced
      },
      futurePotential: {
        fiveYear: financialAnalysis ? [
          `**Revenue Growth:** Projected to reach ${financialAnalysis.revenue_potential_year_5} in revenue within 5 years.`,
          `**Market Penetration:** Expected ${marketAnalysis?.market_penetration_potential || '10%'} market penetration.`,
          `**ROI Achievement:** ${financialAnalysis.roi_potential} ROI with ${financialAnalysis.payback_period} payback period.`
        ] : [
          `**Market Leadership:** Establish dominant position in the ${idea.category} space.`,
          `**Global Expansion:** Deploy across all major markets and regions.`,
          `**Technology Evolution:** Continuous innovation and feature enhancement.`
        ],
        tenYear: [
          `**Market Standard:** Become the industry standard for ${idea.category.toLowerCase()}.`,
          `**Ecosystem Development:** Foster a thriving partner and developer ecosystem.`,
          `**Innovation Platform:** Enable next-generation innovations built on this foundation.`
        ]
      },
      commercialValue: {
        revenueStreams: financialAnalysis ? [
          financialAnalysis.revenue_model,
          financialAnalysis.pricing_strategy,
          `Projected ${financialAnalysis.revenue_potential_year_1} Year 1, ${financialAnalysis.revenue_potential_year_3} Year 3`
        ] : template.revenueStreams,
        costSavings: marketAnalysis ? [
          `Customer acquisition cost: ${marketAnalysis.customer_acquisition_cost}`,
          `Customer lifetime value: ${marketAnalysis.customer_lifetime_value}`,
          `Market growth rate: ${marketAnalysis.market_growth_rate}`
        ] : [
          'Reduce operational costs by 40-60%',
          'Eliminate redundant processes and systems',
          'Accelerate time-to-market for new features'
        ],
        competitiveAdvantages: [
          idea.innovationLevel || 'Breakthrough innovation',
          idea.qualityThresholdCheck || 'Meets all quality thresholds',
          marketAnalysis?.competitive_landscape || 'Superior to existing solutions'
        ]
      },
      innovationHighlights: {
        technical: individualScores
          .filter((score: any) => score.criterion === 'Technical Feasibility' || score.criterion === 'Innovation Potential')
          .map((score: any) => score.justification)
          .slice(0, 3),
        industry: individualScores
          .filter((score: any) => score.criterion === 'Market Impact' || score.criterion === 'Competitive Advantage')
          .map((score: any) => score.justification)
          .slice(0, 3)
      },
      comparisonToOthers: template.comparison,
      implementationRoadmap: financialAnalysis ? [
        `Phase 1 (Initial Investment): ${financialAnalysis.investment_required} investment for ${financialAnalysis.cost_structure}`,
        `Phase 2 (Break-even): Achieve break-even in ${financialAnalysis.break_even_timeline}`,
        `Phase 3 (Scale): Target ${financialAnalysis.net_present_value} NPV with full market deployment`
      ] : [
        `Phase 1: Foundation and proof-of-concept development`,
        `Phase 2: Pilot deployment and market validation`,
        `Phase 3: Full-scale rollout and market expansion`
      ],
      technicalDeepDive: {
        architecture: `Implementation approach: ${idea.implementationComplexity}. ${idea.riskAssessment}`,
        techStack: template.technicalDeepDive?.techStack || {
          frontend: 'Modern web technologies',
          backend: 'Scalable cloud architecture',
          database: 'Distributed data management',
          infra: 'Cloud-native infrastructure'
        },
        integrationPoints: marketAnalysis ? [
          marketAnalysis.go_to_market_strategy,
          'Integration with existing enterprise systems',
          'API ecosystem for third-party developers'
        ] : template.technicalDeepDive?.integrationPoints || []
      },
      resourceAllocation: {
        team: financialAnalysis ? 
          `Investment focus: ${financialAnalysis.cost_structure}` :
          'Cross-functional team with domain expertise',
        budgetNote: financialAnalysis ?
          `Total investment: ${financialAnalysis.investment_required} with ${financialAnalysis.break_even_timeline} to break-even` :
          'Budget allocation based on phased implementation approach'
      },
      trizFlow: {
        contradiction: idea.implementationComplexity ? 
          `Balancing ${idea.implementationComplexity.toLowerCase()} implementation with achieving ${idea.keyStrengths?.[0] || 'key benefits'}` :
          `Improving solution capabilities vs. reducing implementation complexity`,
        principle: idea.originalSource ? 
          `Leveraging innovation from ${idea.originalSource} to address core challenges` :
          `Applying systematic innovation principles to resolve conflicts`,
        implementation: idea.innovationLevel ?
          `${idea.innovationLevel} approach: ${idea.description}` :
          `Develop a modular, scalable architecture for phased implementation`,
        benefit: individualScores.length > 0 ?
          individualScores.find((s: any) => s.criterion === 'Market Impact')?.justification || 
          `Achieves significant market impact through innovative solution design` :
          `Delivers breakthrough results while managing risks effectively`
      },
      marketResearch: {
        marketSize: marketAnalysis?.target_market_size || marketResearch?.marketSize || 'Significant market opportunity',
        competitorAnalysis: marketAnalysis?.competitive_landscape || marketResearch?.competitorAnalysis || 'Limited direct competition',
        targetSegment: marketAnalysis?.go_to_market_strategy || marketResearch?.targetSegment || 'Enterprise customers',
        revenueModel: financialAnalysis?.revenue_model || marketResearch?.revenueModel || 'Subscription-based',
        timeToMarket: timeToMarket,
        riskFactors: financialAnalysis?.financial_risk_factors || marketResearch?.riskFactors || ['Market adoption risk', 'Technical complexity', 'Regulatory changes']
      },
      keyPoints: idea.keyStrengths || keyValuePoints || [
        `Addresses critical market need with ${idea.innovationLevel} innovation`,
        `Strong competitive positioning with unique value proposition`,
        `Clear path to profitability with attractive unit economics`,
        `Scalable solution with global market potential`
      ],
      detailedAnalysis: individualScores.length > 0 ?
        individualScores.map((score: any) => `${score.criterion}: ${score.justification}`).join(' ') :
        `This idea ranks #${rank} due to its ${idea.innovationLevel} innovation level and strong performance across evaluation criteria.`
    };
  };

  const generateGameRules = () => {
    return {
      scoringMethod: 'Multi-criteria weighted scoring with pairwise comparison',
      eliminationRounds: Math.ceil(Math.log2(ideas.length)),
      winnerSelection: 'Top 10 ideas based on weighted total score',
      tieBreaking: 'Innovation score takes precedence',
      validationCriteria: [
        'Must align with business objectives',
        'Technical feasibility score > 6.0',
        'Innovation score > 7.0',
        'Implementation timeline < 18 months'
      ]
    };
  };

  const scoreIdea = (idea: any, criterion: EvaluationCriteria): number => {
    // Use actual scores if available from parsed data
    if (idea.score && idea.score.scores) {
      const score = idea.score.scores[criterion.id];
      if (typeof score === 'number' && !isNaN(score)) {
        return score;
      }
    }
    
    // Fallback to simulated scoring
    let baseScore = 0;
    
    switch (criterion.id) {
      case 'innovation':
        baseScore = idea.innovationScore || (7 + Math.random() * 2.5);
        break;
      case 'feasibility':
        baseScore = idea.feasibilityScore || (6 + Math.random() * 3);
        break;
      case 'impact':
        baseScore = idea.impactScore || (6.5 + Math.random() * 3);
        break;
      case 'cost':
        baseScore = idea.costScore || (7 + Math.random() * 2);
        break;
      case 'implementation':
        baseScore = 7 + Math.random() * 2;
        break;
      case 'strategic':
        baseScore = 7.5 + Math.random() * 2;
        break;
      case 'security':
        baseScore = 7 + Math.random() * 2;
        break;
      case 'competitive':
        baseScore = 7 + Math.random() * 2.5;
        break;
      case 'sustainability':
        baseScore = 6.5 + Math.random() * 2.5;
        break;
    }
    
    return Math.min(10, Math.max(1, baseScore));
  };

  const generateTournamentMatches = (ideas: any[]): TournamentMatch[] => {
    const matches: TournamentMatch[] = [];
    const totalRounds = Math.ceil(Math.log2(ideas.length));
    
    // Create first round matches
    for (let i = 0; i < ideas.length - 1; i += 2) {
      if (ideas[i + 1]) {
        matches.push({
          id: `match-${i/2}-1`,
          idea1: ideas[i],
          idea2: ideas[i + 1],
          winner: null,
          criteria: criteria[Math.floor(Math.random() * criteria.length)].id,
          round: 1
        });
      }
    }
    
    return matches;
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  
  const processStageProgressive = async (stageIndex: number, stageFunction: () => Promise<void>) => {
    const stage = stages[stageIndex];
    setCurrentStage(stageIndex);
    
    // Progressive stage processing with streaming progress
    await new Promise(resolve => {
      let progress = stageIndex > 0 ? stages[stageIndex - 1].progress : 0;
      const increment = (stage.progress - progress) / 10;
      const adjustedDuration = stage.duration;
      
      const updateProgress = () => {
        progress += increment;
        setStageProgress(Math.min(progress, stage.progress));
        
        if (progress >= stage.progress) {
          resolve(undefined);
        } else {
          setTimeout(updateProgress, adjustedDuration / 10);
        }
      };
      updateProgress();
    });
    
    // Execute stage function
    await stageFunction();
  };

  const processEvaluationProgressive = async () => {
    const addLog = (message: string) => {
      setEvaluationLogs(prev => [...prev, message]);
    };

    try {
      // Stage 1: Game Rules Creation
      await processStageProgressive(0, async () => {
        setCurrentProcessingStep('Creating game rules from problem definition...');
        addLog('🎯 Stage 1: Game Rules Creation - STARTING');
        addLog('');
        const dataSource = top10Ideas && top10Ideas.length > 0 ? 'parsed TRIZ analysis data' : 'comprehensive innovation analysis';
        addLog(`📊 Data Source Confirmed: Using ${dataSource}`);
        addLog('📋 Analyzing problem view and constraints...');
        
        const rules = generateGameRules();
        setGameRules(rules);
        addLog('✅ Derived evaluation criteria from "Definition of Good" parameters');
        addLog(`✅ Established ${criteria.length} scoring dimensions with weighted importance`);
        addLog('✅ Created tournament elimination structure');
        addLog(`✅ Set validation thresholds: Innovation > 7.0, Feasibility > 6.0`);
        addLog('');
      });

      // Stage 2: Tournament Setup
      await processStageProgressive(1, async () => {
        setCurrentProcessingStep('Setting up tournament brackets...');
        addLog('⚔️ Stage 2: Tournament Setup - STARTING');
        addLog('');
        
        await delay(600);
        
        const brackets = generateTournamentMatches(innovationIdeas.slice(0, 16));
        setTournamentBrackets(brackets);
        addLog(`✅ Organized ${innovationIdeas.length} ideas into tournament structure`);
        addLog(`✅ Created ${brackets.length} head-to-head comparison matches`);
        addLog('✅ Prepared pairwise evaluation matrix');
        addLog('');
      });

      // Stage 3: Multi-parameter Scoring
      await processStageProgressive(2, async () => {
        setCurrentProcessingStep('Calculating multi-dimensional scores...');
        addLog('📊 Stage 3: Multi-parameter Scoring - STARTING');
        addLog('');
        
        // Add parsing status information
        const dataSource = top10Ideas && top10Ideas.length > 0 ? 'parsed TRIZ analysis data' : 'comprehensive innovation analysis';
        const ideasCount = innovationIdeas.length;
        
        if (top10Ideas && top10Ideas.length > 0) {
          const ranks = top10Ideas.map(idea => idea.rank).sort((a, b) => a - b);
          const missingRanks = Array.from({length: 10}, (_, i) => i + 1).filter(rank => !ranks.includes(rank));
          
          addLog(`📌 Parsing Status: Successfully parsed ${top10Ideas.length} ideas from TRIZ agent`);
          addLog(`   Found ranks: ${ranks.join(', ')}`);
          if (missingRanks.length > 0) {
            addLog(`   ⚠️ Missing ranks: ${missingRanks.join(', ')}`);
            addLog(`   Note: Some agent blocks may have been malformed or missing`);
          }
          addLog('');
        } else {
          addLog(`📌 Using hardcoded fallback data (TRIZ parsing unsuccessful)`);
          if (parsingError) {
            addLog(`   Parsing error: ${parsingError}`);
          }
          addLog('');
        }
        
        await delay(1000);
        
        // Use hardcoded scoring data
        const matrix: Array<{
          ideaId: string;
          title: string;
          scores: Record<string, number>;
          weightedTotal: number;
        }> = [];
        
                 innovationIdeas.forEach((idea: any, index) => {
           addLog(`🔬 Evaluating Idea ${index + 1}: "${idea.title}"`);
           
           const scoreData = idea.score;
           
           // Show individual scores with actual values
           criteria.forEach(criterion => {
             const score = (scoreData.scores as any)[criterion.id];
             if (typeof score !== 'number' || isNaN(score)) {
                throw new Error(`Invalid or missing score for criterion '${criterion.name}' in idea '${idea.title}'. Received value: ${score} of type ${typeof score}`);
             }
             addLog(`   ${criterion.name}: ${score.toFixed(1)}/10.0 (weight: ${(criterion.weight * 100).toFixed(0)}%)`);
           });
          
          // Show rank and reasoning
          addLog(`   🏆 Rank: #${scoreData.rank}`);
          addLog(`   📝 Reasoning: ${scoreData.reasoning}`);
          
          matrix.push({
            ideaId: idea.id,
            title: idea.title,
            scores: scoreData.scores,
            weightedTotal: scoreData.totalScore
          });
          
          if (typeof scoreData.totalScore !== 'number' || isNaN(scoreData.totalScore)) {
            throw new Error(`Invalid or missing total score for idea '${idea.title}'. Received value: ${scoreData.totalScore} of type ${typeof scoreData.totalScore}`);
          }
          addLog(`   💡 Weighted Total Score: ${scoreData.totalScore.toFixed(2)}/10.0`);
          
          // Show key strengths and areas for improvement if available
          if (idea.keyStrengths && idea.keyStrengths.length > 0) {
            addLog(`   ✨ Key Strengths: ${idea.keyStrengths.slice(0, 2).join(', ')}`);
          }
          if (idea.areasForImprovement && idea.areasForImprovement.length > 0) {
            addLog(`   🔧 Areas for Improvement: ${idea.areasForImprovement.slice(0, 2).join(', ')}`);
          }
          
          addLog('');
        });
        
        setScoringMatrix(matrix);
        addLog('✅ Generated comprehensive scoring matrix');
        addLog(`✅ Calculated weighted scores across ${criteria.length} evaluation dimensions`);
        addLog('');
        
        // Store scores for next stage
        (window as any).evaluationScores = innovationIdeas.map(idea => idea.score);
      });

      // Stage 4: Tournament Competition & Ideality Scoring
      await processStageProgressive(3, async () => {
        setCurrentProcessingStep('Running tournament competition...');
        addLog('🥇 Stage 4: Tournament Competition & Ideality Scoring - STARTING');
        addLog('');
        
        await delay(800);
        
        const scores = innovationIdeas.map(idea => idea.score);
        
        // Calculate ideality scores (TRIZ concept)
        const idealityScoreList = scores.map((score: any) => {
          const benefits = (score.scores.innovation + score.scores.impact) / 2;
          const costs = (10 - score.scores.cost) / 10;
          const harm = (10 - score.scores.security) / 10;
          const idealityScore = benefits / (costs + harm + 0.1);
          
          return {
            ideaId: score.ideaId,
            benefits,
            costs,
            harm,
            idealityScore,
            totalScore: score.totalScore
          };
        });
        
        setIdealityScores(idealityScoreList);
        addLog('🔥 Calculated TRIZ Ideality Scores (Benefits / (Cost + Harm))');
        idealityScoreList.slice(0, 5).forEach((idea: any, index: any) => {
          const ideaTitle = innovationIdeas.find(i => i.id === idea.ideaId)?.title || 'Unknown';
          addLog(`   ${index + 1}. ${ideaTitle}: Ideality = ${idea.idealityScore.toFixed(2)}`);
        });
        addLog('');
        
        // Run tournament matches
        const brackets = tournamentBrackets;
        addLog('⚡ Executing head-to-head tournament matches...');
        brackets.forEach((match, index) => {
          const score1 = scores.find((s: any) => s.ideaId === match.idea1.id)?.totalScore || 0;
          const score2 = scores.find((s: any) => s.ideaId === match.idea2.id)?.totalScore || 0;
          const winner = score1 > score2 ? match.idea1.title : match.idea2.title;
          addLog(`   Match ${index + 1}: ${match.idea1.title} vs ${match.idea2.title} → Winner: ${winner}`);
        });
        
        addLog('');
        
        // Store scores for final stage
        (window as any).finalScores = scores;
      });

      // Stage 5: Final Ranking & Selection
      await processStageProgressive(4, async () => {
        setCurrentProcessingStep('Generating final rankings...');
        addLog('🎖️ Stage 5: Final Ranking & Top 10 Selection - STARTING');
        addLog('');
        
        await delay(600);
        
        const scores = innovationIdeas.map(idea => idea.score);
        
        const dataSource = top10Ideas && top10Ideas.length > 0 ? 'parsed TRIZ analysis data' : 'comprehensive innovation analysis';
        addLog(`🏆 FINAL RANKINGS - TOP 10 BREAKTHROUGH IDEAS (from ${dataSource}):`);
        addLog('');
        scores.slice(0, 10).forEach((score: any, index: number) => {
          const idea = innovationIdeas.find(i => i.id === score.ideaId);
          const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅';
          addLog(`${medal} #${index + 1} - ${idea?.title} (Score: ${score.totalScore.toFixed(2)})`);
          addLog(`     Innovation: ${score.scores.innovation?.toFixed(1)}, Feasibility: ${score.scores.feasibility?.toFixed(1)}, Impact: ${score.scores.impact?.toFixed(1)}`);
        });
        
        addLog('');
        addLog('✅ Automatically bubbled up top 10 ideas without manual intervention');
        addLog('✅ Generated executive recommendation matrix');
        addLog('✅ Created detailed justification for each ranking');
        addLog('');
        addLog('🎉 Tournament-style evaluation completed successfully!');

        setEvaluationCriteria(criteria);
        setIdeaScores(scores);
        setTournamentMatches(tournamentBrackets);
        setCurrentProcessingStep('Evaluation complete!');
      });
      
      // Final completion
      setIsProcessing(false);
      setIsEvaluationCompleted(true);
      
    } catch (error) {
      addLog('❌ Error during evaluation process');
      if (error instanceof Error) {
        addLog(`DETAILS: ${error.message}`);
        console.error("Evaluation process error:", error);
      }
      setIsProcessing(false);
    }
  };

  const handleComplete = () => {
    const top10Ideas = innovationIdeas.slice(0, 10);
    
    onComplete({
      topIdeas: top10Ideas.map(idea => idea.score),
      evaluationCriteria: evaluationCriteria.length > 0 ? evaluationCriteria : criteria,
      fullResults: innovationIdeas.map(idea => idea.score),
      tournamentResults: tournamentMatches,
      evaluationLogs,
      ideaScores,
      summary: {
        totalEvaluated: innovationIdeas.length,
        selectedIdeas: 10,
        avgTopScore: top10Ideas.reduce((sum, idea) => sum + idea.score.totalScore, 0) / top10Ideas.length,
        winnerCategory: innovationIdeas[0]?.category || 'AI Enhancement',
        evaluationInsights: [
          'Privacy-preserving AI innovations scored highest overall',
          'Security & Compliance solutions showed strong market demand',
          'Core Technology solutions demonstrated best feasibility scores',
          'AI Enhancement innovations offer highest transformation potential'
        ]
      }
    });
  };

  const startEvaluation = () => {
    setIsProcessing(true);
    const dataSource = top10Ideas && top10Ideas.length > 0 ? 'parsed TRIZ analysis data' : 'comprehensive innovation analysis';
    setEvaluationLogs([
      '🎯 Initializing Idea Evaluation Engine...',
      `📊 Data Source: Using ${dataSource}`,
      `💡 Evaluating ${innovationIdeas.length} breakthrough innovation ideas`,
      ''
    ]);
    setStageProgress(0);
    setCurrentStage(0);
    
    // Start progressive processing
    processEvaluationProgressive();
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'from-yellow-400 to-yellow-600';
    if (rank <= 3) return 'from-slate-300 to-slate-500';
    if (rank <= 10) return 'from-orange-400 to-orange-600';
    return 'from-slate-600 to-slate-800';
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return Crown;
    if (rank <= 3) return Medal;
    if (rank <= 10) return Award;
    return Star;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
      <div className="max-w-7xl mx-auto p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="w-16 h-16 sqsft-gradient-primary rounded-3xl flex items-center justify-center shadow-2xl">
              <Trophy className="w-8 h-8 text-white" />
          </div>
          <div>
              <h2 className="text-4xl lg:text-5xl sqsft-heading-1 mb-2">
                <span className="sqsft-gradient-text">Idea Evaluation</span>
              </h2>
              <p className="text-lg sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                Systematic idea analysis using a multi-criteria decision framework.
              </p>
          </div>
        </div>
        
        {!isProcessing && !isEvaluationCompleted && (
          <div className="space-y-4">
            
            <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              onClick={startEvaluation}
                className="sqsft-btn-primary px-6 py-3"
            >
              Start Evaluation
            </motion.button>
          </div>
        )}
        
        {isEvaluationCompleted && (
          <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            onClick={handleComplete}
              className="sqsft-btn-primary px-6 py-3"
          >
            Complete Phase
          </motion.button>
        )}
      </div>

      {/* Processing Status */}
      {isProcessing && (
          <div className="sqsft-feature-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="sqsft-heading-4">
                Stage {currentStage + 1}/5: {stages[currentStage]?.name}
              </div>
                <div className="sqsft-caption mt-1" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                  {currentProcessingStep}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="font-bold text-xl sqsft-gradient-text">
                  {stageProgress.toFixed(0)}%
                </span>
              </div>
            </div>
            <div className="w-full rounded-full h-3" style={{ backgroundColor: 'rgb(var(--color-border))' }}>
            <motion.div
                className="h-3 rounded-full sqsft-gradient-primary shadow-lg"
              initial={{ width: 0 }}
              animate={{ width: `${stageProgress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      )}

      {/* Completion Status - Only show if no logs available */}
      {isEvaluationCompleted && !isProcessing && evaluationLogs.length === 0 && (
          <div className="sqsft-feature-card p-6 border-2" style={{ borderColor: 'rgb(var(--color-sage-green))', backgroundColor: 'rgba(66, 102, 83, 0.05)' }}>
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="sqsft-heading-3">
                  <span className="sqsft-gradient-text">Tournament-Style Evaluation Complete!</span>
                </h3>
                <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                  Successfully evaluated {innovationIdeas.length} breakthrough ideas using {top10Ideas && top10Ideas.length > 0 ? 'parsed TRIZ data' : 'comprehensive innovation analysis'}. 
                  Top {Math.min(10, innovationIdeas.length)} solutions ranked and ready for strategic implementation.
                </p>
              </div>
            </div>
        </div>
      )}

      {/* Evaluation Results */}
      {(isProcessing || isEvaluationCompleted || evaluationLogs.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Evaluation Logs */}
          <div className="sqsft-feature-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="sqsft-heading-3">
              <span className="sqsft-gradient-text">Live Evaluation Process</span>
            </h3>
            {evaluationLogs.length > 0 && top10Ideas && top10Ideas.length > 0 && top10Ideas.length < 10 && (
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                  ⚠️ Only {top10Ideas.length} of 10 parsed
                </span>
              </div>
            )}
          </div>
            <div className="rounded-xl p-4 h-96 overflow-y-auto font-mono text-sm" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }} ref={logsRef}>
            {evaluationLogs.length > 0 ? (
              <div>
                {evaluationLogs.map((log, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                    className={`${
                      log.startsWith('✅')
                        ? 'text-green-600'
                        : log.startsWith('🎯') || log.startsWith('🎉') || log.startsWith('🏆')
                        ? 'sqsft-gradient-text'
                        : log.startsWith('⚔️') || log.startsWith('📊') || log.startsWith('🥇') || log.startsWith('🎖️')
                        ? 'sqsft-gradient-text'
                        : log.startsWith('🔬') || log.startsWith('🔥') || log.startsWith('⚡')
                        ? 'text-purple-600'
                        : log.startsWith('🥇') || log.startsWith('🥈') || log.startsWith('🥉') || log.startsWith('🏅')
                        ? 'sqsft-gradient-text-gold'
                        : 'text-slate-700'
                    } leading-relaxed`}
                >
                  {log}
                </motion.div>
              ))}
              
              {/* Show completion summary at the end of logs */}
              {isEvaluationCompleted && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">Tournament-Style Evaluation Complete!</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    Successfully evaluated {innovationIdeas.length} breakthrough ideas using {top10Ideas && top10Ideas.length > 0 ? 'parsed TRIZ data' : 'comprehensive innovation analysis'}. 
                    Top {Math.min(10, innovationIdeas.length)} solutions ranked and ready for strategic implementation.
                  </p>
                </div>
              )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 sqsft-gradient-primary rounded-2xl flex items-center justify-center mb-4">
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <h4 className="sqsft-heading-4 mb-2">Evaluation Engine</h4>
                <p className="sqsft-body max-w-xs" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                  The live evaluation process will be shown here once initiated. Click "Start Evaluation" to begin.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Game Rules & Evaluation Criteria */}
          <div className="sqsft-feature-card p-6">
          <h3 className="sqsft-heading-3 mb-4">
            <span className="sqsft-gradient-text">Evaluation Framework</span>
          </h3>
            <div className="h-96 overflow-y-auto pr-2">
            {gameRules && (
              <div className="mb-6">
                  <h4 className="sqsft-heading-4 mb-3">Evaluation Protocol</h4>
                  <div className="rounded-xl p-4 border-2 mb-4" style={{ backgroundColor: 'rgba(66, 102, 83, 0.05)', borderColor: 'rgb(var(--color-sage-green))' }}>
                    <div className="space-y-2 text-sm sqsft-body text-slate-700">
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Scoring Method:</strong> {gameRules.scoringMethod}
                  </div>
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Elimination Rounds:</strong> {gameRules.eliminationRounds}
                </div>
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Winner Selection:</strong> {gameRules.winnerSelection}
                      </div>
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Tie Breaking:</strong> {gameRules.tieBreaking}
                      </div>
                    </div>
                </div>
              </div>
            )}
            
            {(evaluationCriteria.length > 0 || !gameRules) && (
              <div>
                  <h4 className="sqsft-heading-4 mb-3">{!gameRules ? 'Core Evaluation Criteria' : 'Weighted Evaluation Criteria'}</h4>
                <div className="space-y-3">
                  {(gameRules ? evaluationCriteria : criteria).map((criterion, index) => (
                    <motion.div
                      key={criterion.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                        className="sqsft-feature-card p-4 border hover:border-sage-green transition-all duration-300"
                    >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                            <criterion.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <h5 className="sqsft-heading-4">{criterion.name}</h5>
                            <p className="sqsft-caption" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                              {criterion.description}
                            </p>
                        </div>
                        
                        {/* Always show weight */}
                        <div className="text-center">
                          <div className="font-bold text-lg sqsft-gradient-text">
                            {(criterion.weight * 100).toFixed(0)}%
                          </div>
                          <div className="sqsft-caption" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            Weight
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            
              {!gameRules && (
                <div className="mt-6">
                  <h4 className="sqsft-heading-4 mb-3 text-slate-500">Evaluation Protocol (Awaiting Generation)</h4>
                  <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/80 space-y-2 text-sm text-slate-500">
                    <div><strong className="text-slate-600">Scoring Method:</strong> Will be defined upon starting...</div>
                    <div><strong className="text-slate-600">Elimination Rounds:</strong> Will be calculated based on idea count...</div>
                    <div><strong className="text-slate-600">Winner Selection:</strong> Top ideas from scoring...</div>
                    <div><strong className="text-slate-600">Tie Breaking:</strong> Precedence rules to be set...</div>
                  </div>
                </div>
            )}
          </div>
        </div>
      </div>
      )}

        {/* Results Rankings */}
      <AnimatePresence>
        {ideaScores.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
          >
            {/* Tournament Winner Spotlight */}
              <div className="group relative">
                 <div className="absolute -inset-2 sqsft-gradient-gold rounded-3xl opacity-0 group-hover:opacity-40 blur-2xl transition-all duration-500 group-hover:blur-3xl"></div>
                 <div className="relative sqsft-feature-card p-8 border-2 transition-all duration-300 group-hover:border-transparent group-hover:shadow-2xl group-hover:scale-[1.01] group-hover:-translate-y-1" style={{ borderColor: 'rgb(var(--color-royal-gold))' }}>
                  <div className="flex items-center space-x-6 mb-6">
                    <div className="w-16 h-16 sqsft-gradient-gold rounded-3xl flex items-center justify-center shadow-2xl">
                      <Crown className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="sqsft-heading-2">
                        <span className="sqsft-gradient-text-gold">Tournament Champion</span>
                      </h3>
                      <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        Highest-scoring innovation breakthrough
                      </p>
                    </div>
                  </div>
                  
                  {(() => {
                    const championIdea = innovationIdeas[0];
                    if (!championIdea) return null;
                    
                    return (
                        <div className="rounded-xl p-6" style={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          <div className="lg:col-span-2">
                               <h3 className="sqsft-heading-3 flex items-center space-x-2 mb-4">
                                 <div className="w-6 h-6 sqsft-gradient-gold rounded-lg flex items-center justify-center">
                                   <Trophy className="w-4 h-4 text-white" />
                                 </div>
                                 <span className="sqsft-gradient-text">{championIdea.title}</span>
                               </h3>
                               <p className="sqsft-body mb-4" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                                 {championIdea.description}
                               </p>
                               
                               {/* Key Strengths for Champion */}
                               {championIdea.keyStrengths && championIdea.keyStrengths.length > 0 && (
                                 <div className="mb-4">
                                   <h5 className="text-sm font-semibold mb-2 flex items-center space-x-2" style={{ color: 'rgb(var(--color-royal-gold))' }}>
                                     <Star className="w-4 h-4" />
                                     <span>Key Strengths:</span>
                                   </h5>
                                   <div className="flex flex-wrap gap-2">
                                     {championIdea.keyStrengths.map((strength: string, idx: number) => (
                                       <span
                                         key={idx}
                                         className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border"
                                         style={{ 
                                           backgroundColor: 'rgba(212, 175, 55, 0.15)', 
                                           borderColor: 'rgba(212, 175, 55, 0.4)',
                                           color: 'rgb(var(--color-royal-gold))'
                                         }}
                                       >
                                         {strength}
                                       </span>
                                     ))}
                                   </div>
                                 </div>
                               )}
                               
                               <div className="mt-4">
                                 <h4 className="sqsft-heading-4 mb-2 flex items-center space-x-2">
                                    <div className="w-5 h-5 sqsft-gradient-primary rounded-lg flex items-center justify-center">
                                      <Rocket className="w-3 h-3 text-white" />
                                    </div>
                                    <span className="sqsft-gradient-text">Why This Is The #1 Privacy Innovation</span>
                                  </h4>
                                 <div className="space-y-2 sqsft-body">
                                    {championIdea.keyStrengths && championIdea.keyStrengths.length > 0 ? (
                                      championIdea.keyStrengths.slice(0, 3).map((strength: string, idx: number) => (
                                        <p key={idx} className="text-sm text-slate-700">
                                          <strong style={{ color: 'rgb(var(--color-deep-green))' }}>{strength}:</strong> {
                                            strength.toLowerCase().includes('privacy') ? 'Revolutionary approach to privacy-preserving technology that enables secure data processing without exposure.' :
                                            strength.toLowerCase().includes('breakthrough') || strength.toLowerCase().includes('technology') ? 'Combines cutting-edge technologies to achieve what was previously impossible in the industry.' :
                                            strength.toLowerCase().includes('market') ? 'Unlocks massive market opportunities in regulated industries like healthcare and finance.' :
                                            strength.toLowerCase().includes('differentiation') ? 'Provides significant competitive advantages and market positioning.' :
                                            strength.toLowerCase().includes('quantum') ? 'Future-proof security architecture designed to withstand quantum computing threats.' :
                                            strength.toLowerCase().includes('compliance') ? 'Built-in regulatory compliance reducing legal risks and operational overhead.' :
                                            strength.toLowerCase().includes('automation') || strength.toLowerCase().includes('ai') ? 'Advanced automation capabilities that transform operational efficiency.' :
                                            'Delivers exceptional value and innovation in the privacy technology space.'
                                          }
                                        </p>
                                      ))
                                    ) : (
                                      <>
                                        <p dangerouslySetInnerHTML={{ __html: '**Breakthrough Technology Convergence:** Combines homomorphic encryption with machine learning to achieve what was previously impossible.'.replace(/\*\*(.*?)\*\*/g, '<strong style="color: rgb(var(--color-deep-green))">$1</strong>') }}></p>
                                        <p dangerouslySetInnerHTML={{ __html: '**Massive Market Potential:** Unlocks AI in regulated industries like healthcare and finance, a $100B+ opportunity.'.replace(/\*\*(.*?)\*\*/g, '<strong style="color: rgb(var(--color-deep-green))">$1</strong>') }}></p>
                                      </>
                                    )}
                                  </div>
                                </div>
                          </div>
                          
                          <div className="space-y-4">
                               <div className="sqsft-feature-card p-4 text-center border border-yellow-300">
                                  <h5 className="sqsft-heading-4">Overall Score</h5>
                                  <p className="text-4xl font-bold sqsft-gradient-text-gold">{championIdea.score.totalScore.toFixed(2)}</p>
                               </div>
                               <h5 className="sqsft-heading-4">Strategic Overview</h5>
                               <div className="space-y-3">
                                 <div className="flex items-center space-x-2 sqsft-body">
                                   <Clock className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                                   <span style={{ color: 'rgb(var(--color-text-secondary))' }}>Time to Implement:</span>
                                   <span className="font-bold sqsft-gradient-text">{championIdea.timeToImplement}</span>
                                </div>
                                 <div className="flex items-center space-x-2 sqsft-body">
                                   <TrendingUp className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                                   <span style={{ color: 'rgb(var(--color-text-secondary))' }}>Impact:</span>
                                   <span className="font-bold sqsft-gradient-text">{championIdea.impact}</span>
                                 </div>
                               </div>
                          </div>
                        </div>
                        <div className="mt-6 text-right">
                          <button
                            onClick={() => {
                              const detailedIdea = generateDetailedIdeaData(championIdea, 1, championIdea.score);
                              setSelectedIdea(detailedIdea);
                            }}
                            className="sqsft-btn-primary inline-flex items-center group"
                          >
                            View Full Analysis
                            <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                  </div>
                  
                {/* Rest of the winners */}
                <div className="sqsft-feature-card p-8">
                  <h3 className="sqsft-heading-3 mb-6">
                    <span className="sqsft-gradient-text">Top 10 Innovation Ideas</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {innovationIdeas.slice(1, 10).map((idea, index) => {
                    const scoreData = idea.score;
                    
                    const RankIcon = getRankIcon(scoreData.rank);
                    
                    return (
                      <motion.div
                        key={idea.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="sqsft-feature-card p-4 h-full flex flex-col border hover:border-sage-green transition-all duration-300"
                        >
                          <div className="flex items-start space-x-3 mb-3">
                            <div className="flex items-center space-x-3 flex-grow">
                              <div className="w-8 h-8 sqsft-gradient-primary rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                                <RankIcon className="w-4 h-4 text-white" />
                              </div>
                              <h4 className="sqsft-heading-4 flex-1">{idea.title}</h4>
                            </div>
                            <div className="text-center flex-shrink-0 pl-2">
                              <div className="font-bold text-lg sqsft-gradient-text">{scoreData.totalScore.toFixed(2)}</div>
                              <div className="sqsft-caption" style={{ color: 'rgb(var(--color-text-secondary))' }}>Score</div>
                            </div>
                        </div>
                          <p className="sqsft-body mb-4 flex-grow" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            {idea.description}
                          </p>
                          
                          {/* Key Strengths */}
                          {idea.keyStrengths && idea.keyStrengths.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-xs font-semibold text-slate-600 mb-2 flex items-center space-x-1">
                                <Star className="w-3 h-3" style={{ color: 'rgb(var(--color-sage-green))' }} />
                                <span>Key Strengths:</span>
                              </h5>
                              <div className="flex flex-wrap gap-1">
                                {idea.keyStrengths.slice(0, 3).map((strength: string, idx: number) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
                                    style={{ 
                                      backgroundColor: 'rgba(66, 102, 83, 0.1)', 
                                      borderColor: 'rgba(66, 102, 83, 0.3)',
                                      color: 'rgb(var(--color-sage-green))'
                                    }}
                                  >
                                    {strength}
                                  </span>
                                ))}
                                {idea.keyStrengths.length > 3 && (
                                  <span className="text-xs text-slate-500">+{idea.keyStrengths.length - 3} more</span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-end">
                            <button
                              onClick={() => {
                                const detailedIdea = generateDetailedIdeaData(idea, scoreData.rank, scoreData);
                                setSelectedIdea(detailedIdea);
                              }}
                              className="sqsft-btn-secondary text-sm px-4 py-2"
                            >
                              View Details
                            </button>
                              </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
      </AnimatePresence>

        {/* Modal */}
      <AnimatePresence>
        {selectedIdea && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedIdea(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="sqsft-feature-card rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header - Clean & Modern */}
              <div className="relative">
                {/* Subtle gradient background */}
                <div className="absolute inset-0 sqsft-gradient-subtle"></div>
                
                {/* Header Content */}
                <div className="relative px-8 pt-8 pb-6">
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex-1">
                      {/* Title and Description */}
                      <h2 className="sqsft-heading-2 mb-2">
                        <span className="sqsft-gradient-text">{selectedIdea.title}</span>
                      </h2>
                      <p className="sqsft-body text-lg" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        {selectedIdea.description}
                      </p>
                      
                      {/* Key Metrics Bar */}
                      <div className="flex items-center gap-6 mt-4">
                        <div className="flex items-center gap-2">
                          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r ${getRankColor(selectedIdea.score.rank)}`}>
                            {(() => {
                              const RankIcon = getRankIcon(selectedIdea.score.rank);
                              return <RankIcon className="w-4 h-4" />;
                            })()}
                            <span>Rank #{selectedIdea.score.rank}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1.5 text-sm">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 fill-current" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                            <Star className="w-4 h-4 fill-current" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                            <Star className="w-4 h-4 fill-current" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                            <Star className="w-4 h-4 fill-current" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                            <Star className="w-4 h-4" style={{ color: 'rgb(var(--color-royal-gold))', fill: `url(#star-gradient-${selectedIdea.score.totalScore})` }} />
                            <defs>
                              <linearGradient id={`star-gradient-${selectedIdea.score.totalScore}`}>
                                <stop offset={`${(selectedIdea.score.totalScore % 1) * 100}%`} stopColor="rgb(var(--color-royal-gold))" />
                                <stop offset={`${(selectedIdea.score.totalScore % 1) * 100}%`} stopColor="transparent" />
                              </linearGradient>
                            </defs>
                          </div>
                          <span className="font-semibold sqsft-gradient-text ml-1">{selectedIdea.score.totalScore.toFixed(1)}</span>
                          <span style={{ color: 'rgb(var(--color-text-secondary))' }}>/10</span>
                        </div>
                        
                        {selectedIdea.innovationLevel && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: 'rgb(var(--color-royal-gold))' }}>
                            <Sparkles className="w-4 h-4" />
                            <span>{selectedIdea.innovationLevel}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Close Button */}
                    <button
                      onClick={() => setSelectedIdea(null)}
                      className="sqsft-btn-ghost w-10 h-10 rounded-xl flex items-center justify-center transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs Navigation - Modern Design */}
              <div className="px-8">
                <div className="flex gap-1 border-b" style={{ borderColor: 'rgb(var(--color-border))' }}>
                  {[
                    { id: 'overview', label: 'Overview', icon: Trophy },
                    { id: 'analysis', label: 'Analysis', icon: BarChart3 },
                    { id: 'innovation', label: 'Innovation', icon: Lightbulb },
                    { id: 'business', label: 'Business Case', icon: DollarSign },
                    { id: 'implementation', label: 'Implementation', icon: Rocket }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        (window as any).activeIdeaTab = tab.id;
                        // Force re-render
                        setSelectedIdea({...selectedIdea});
                      }}
                      className={`
                        relative flex items-center gap-2 px-4 py-3 font-medium text-sm transition-all
                        ${((window as any).activeIdeaTab || 'overview') === tab.id
                          ? 'sqsft-gradient-text'
                          : 'sqsft-body hover:opacity-80'
                        }
                      `}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                      {((window as any).activeIdeaTab || 'overview') === tab.id && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 sqsft-gradient-primary"
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="px-8 py-10 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 240px)', backgroundColor: 'rgb(var(--color-bg-light))' }}>
                {/* Overview Tab */}
                {(!(window as any).activeIdeaTab || (window as any).activeIdeaTab === 'overview') && (
                  <div className="space-y-8">
                    {/* Quality Check Status at Top */}
                    {selectedIdea.qualityThresholdCheck && (
                      <div className="flex items-center justify-center">
                        <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full shadow-sm" style={{ backgroundColor: 'rgba(66, 102, 83, 0.1)', borderColor: 'rgb(var(--color-sage-green))', borderWidth: '1px', borderStyle: 'solid' }}>
                          <ShieldCheck className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                          <span className="sqsft-body font-semibold" style={{ color: 'rgb(var(--color-deep-green))' }}>{selectedIdea.qualityThresholdCheck}</span>
                        </div>
                      </div>
                    )}

                    {/* Score Summary Card */}
                    <div className="sqsft-feature-card rounded-2xl p-8">
                      <h3 className="sqsft-heading-3 mb-6 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                        <span className="sqsft-gradient-text">Score Summary</span>
                      </h3>
                      
                      {/* Score Grid - Clean and Modern */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        {[
                          { key: 'Innovation', value: selectedIdea.score.scores.innovation },
                          { key: 'Feasibility', value: selectedIdea.score.scores.feasibility },
                          { key: 'Impact', value: selectedIdea.score.scores.impact },
                          { key: 'Security', value: selectedIdea.score.scores.security },
                          { key: 'Cost', value: selectedIdea.score.scores.cost },
                          { key: 'Strategic', value: selectedIdea.score.scores.strategic }
                        ].map((data, index) => (
                          <div key={data.key} className="sqsft-feature-card rounded-xl p-5 hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-3">
                              <p className="sqsft-body font-medium">{data.key}</p>
                              <span className="text-2xl font-bold sqsft-gradient-text">
                                {data.value.toFixed(1)}
                              </span>
                            </div>
                            <div className="w-full rounded-full h-2" style={{ backgroundColor: 'rgb(var(--color-border))' }}>
                              <div 
                                className="h-2 rounded-full sqsft-gradient-primary transition-all duration-700"
                                style={{ width: `${data.value * 10}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Key Strengths & Areas for Improvement */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                        {selectedIdea.keyStrengths && selectedIdea.keyStrengths.length > 0 && (
                          <div className="sqsft-feature-card rounded-xl p-6" style={{ backgroundColor: 'rgba(66, 102, 83, 0.05)', borderColor: 'rgb(var(--color-sage-green))', borderWidth: '1px', borderStyle: 'solid' }}>
                            <h4 className="sqsft-heading-4 mb-4 flex items-center gap-2" style={{ color: 'rgb(var(--color-deep-green))' }}>
                              <CheckCircle className="w-5 h-5" />
                              Key Strengths
                            </h4>
                            <div className="space-y-3">
                              {selectedIdea.keyStrengths.map((strength: string, idx: number) => (
                                <div key={idx} className="flex items-start gap-3">
                                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(66, 102, 83, 0.2)' }}>
                                    <CheckCircle className="w-3.5 h-3.5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                                  </div>
                                  <span className="sqsft-body text-sm leading-relaxed">{strength}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {selectedIdea.areasForImprovement && selectedIdea.areasForImprovement.length > 0 && (
                          <div className="sqsft-feature-card rounded-xl p-6" style={{ backgroundColor: 'rgba(212, 175, 55, 0.05)', borderColor: 'rgb(var(--color-royal-gold))', borderWidth: '1px', borderStyle: 'solid' }}>
                            <h4 className="sqsft-heading-4 mb-4 flex items-center gap-2" style={{ color: 'rgb(var(--color-royal-gold))' }}>
                              <AlertTriangle className="w-5 h-5" />
                              Areas for Improvement
                            </h4>
                            <div className="space-y-3">
                              {selectedIdea.areasForImprovement.map((area: string, idx: number) => (
                                <div key={idx} className="flex items-start gap-3">
                                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)' }}>
                                    <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                                  </div>
                                  <span className="sqsft-body text-sm leading-relaxed">{area}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>


                  </div>
                )}

                {/* Analysis Tab */}
                {(window as any).activeIdeaTab === 'analysis' && (
                  <div className="space-y-8">
                    {/* Score Justifications Summary */}
                    {selectedIdea.individualScores && selectedIdea.individualScores.length > 0 && (
                      <div className="sqsft-feature-card rounded-xl p-6">
                        <h3 className="sqsft-heading-4 mb-4 flex items-center gap-2">
                          <ClipboardList className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                          <span className="sqsft-gradient-text">Evaluation Insights</span>
                        </h3>
                        <div className="space-y-3">
                          {selectedIdea.individualScores.slice(0, 3).map((scoreItem: any, idx: number) => (
                            <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(66, 102, 83, 0.05)' }}>
                              <div className="flex items-center justify-between mb-1">
                                <p className="sqsft-body font-medium">{scoreItem.criterion}</p>
                                <span className="sqsft-gradient-text font-bold">{scoreItem.score.toFixed(1)}/10</span>
                              </div>
                              <p className="sqsft-caption" style={{ color: 'rgb(var(--color-text-secondary))' }}>{scoreItem.justification}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Key Implementation Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedIdea.timeToImplement && (
                        <div className="sqsft-feature-card rounded-xl p-5 text-center">
                          <Clock className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgb(var(--color-sage-green))' }} />
                          <p className="sqsft-caption font-medium mb-1">Timeline</p>
                          <p className="sqsft-heading-4">{selectedIdea.timeToImplement}</p>
                        </div>
                      )}
                      {selectedIdea.implementationComplexity && (
                        <div className="sqsft-feature-card rounded-xl p-5 text-center">
                          <Cpu className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                          <p className="sqsft-caption font-medium mb-1">Complexity</p>
                          <p className="sqsft-heading-4">{selectedIdea.implementationComplexity}</p>
                        </div>
                      )}
                      {selectedIdea.impact && (
                        <div className="sqsft-feature-card rounded-xl p-5 text-center">
                          <TrendingUp className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgb(var(--color-deep-green))' }} />
                          <p className="sqsft-caption font-medium mb-1">Business Impact</p>
                          <p className="sqsft-heading-4">{selectedIdea.impact}</p>
                        </div>
                      )}
                    </div>

                    {/* Market Analysis */}
                    {selectedIdea.marketAnalysis && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="sqsft-feature-card p-6 border">
                          <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                            <Users className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                            <span>Market Dynamics</span>
                          </h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-600">Market Size</span>
                              <span className="font-semibold text-sm">{selectedIdea.marketAnalysis.target_market_size}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-600">Growth Rate</span>
                              <span className="font-semibold text-sm">{selectedIdea.marketAnalysis.market_growth_rate}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-600">Market Penetration</span>
                              <span className="font-semibold text-sm">{selectedIdea.marketAnalysis.market_penetration_potential}</span>
                            </div>
                          </div>
                        </div>

                        <div className="sqsft-feature-card p-6 border">
                          <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                            <DollarSign className="w-5 h-5" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                            <span>Customer Economics</span>
                          </h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-600">Acquisition Cost</span>
                              <span className="font-semibold text-sm">{selectedIdea.marketAnalysis.customer_acquisition_cost}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-600">Lifetime Value</span>
                              <span className="font-semibold text-sm">{selectedIdea.marketAnalysis.customer_lifetime_value}</span>
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-600 mb-1">Go-to-Market Strategy</p>
                              <p className="text-sm font-semibold">{selectedIdea.marketAnalysis.go_to_market_strategy}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Summary Analysis */}
                    <div className="sqsft-feature-card p-6 border">
                      <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                        <FileText className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                        <span>Executive Summary</span>
                      </h4>
                      <p className="text-sm text-slate-700 leading-relaxed">{selectedIdea.detailedAnalysis}</p>
                    </div>
                  </div>
                )}

                {/* Innovation Tab */}
                {(window as any).activeIdeaTab === 'innovation' && (
                  <div className="space-y-8">

                    {/* Features Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Core Features */}
                      <div className="sqsft-feature-card rounded-xl p-6 border-2" style={{ borderColor: 'rgb(var(--color-sage-green))' }}>
                        <h4 className="sqsft-heading-4 mb-4 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg sqsft-gradient-primary flex items-center justify-center shadow-md">
                            <Code className="w-4 h-4 text-white" />
                          </div>
                          <span className="sqsft-gradient-text">Core Features</span>
                        </h4>
                        <div className="space-y-3">
                          {selectedIdea.revolutionaryFeatures.core.map((feature: string, idx: number) => (
                            <div key={idx} className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(66, 102, 83, 0.05)' }}>
                              <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--color-sage-green))' }} />
                              <p className="sqsft-body text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: feature.replace(/\*\*(.*?)\*\*/g, '<strong style="color: rgb(var(--color-deep-green))">$1</strong>') }}></p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Advanced Capabilities */}
                      <div className="sqsft-feature-card rounded-xl p-6 border-2" style={{ borderColor: 'rgb(var(--color-royal-gold))' }}>
                        <h4 className="sqsft-heading-4 mb-4 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg sqsft-gradient-gold flex items-center justify-center shadow-md">
                            <Zap className="w-4 h-4 text-white" />
                          </div>
                          <span className="sqsft-gradient-text">Advanced Capabilities</span>
                        </h4>
                        <div className="space-y-3">
                          {selectedIdea.revolutionaryFeatures.advanced.map((feature: string, idx: number) => (
                            <div key={idx} className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                              <Star className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                              <p className="sqsft-body text-sm leading-relaxed">{feature}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* TRIZ Analysis */}
                    <div className="sqsft-feature-card rounded-2xl p-8">
                      <h4 className="sqsft-heading-3 mb-6 flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl sqsft-gradient-primary flex items-center justify-center shadow-lg">
                          <Brain className="w-5 h-5 text-white" />
                        </div>
                        <span className="sqsft-gradient-text">TRIZ Innovation Framework</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div className="sqsft-feature-card rounded-xl p-4">
                            <p className="sqsft-caption font-bold uppercase tracking-wider mb-2" style={{ color: 'rgb(var(--color-deep-green))' }}>Core Contradiction</p>
                            <p className="sqsft-body text-sm leading-relaxed">{selectedIdea.trizFlow.contradiction}</p>
                          </div>
                          <div className="sqsft-feature-card rounded-xl p-4">
                            <p className="sqsft-caption font-bold uppercase tracking-wider mb-2" style={{ color: 'rgb(var(--color-deep-green))' }}>TRIZ Principle Applied</p>
                            <p className="sqsft-body text-sm leading-relaxed">{selectedIdea.trizFlow.principle}</p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="sqsft-feature-card rounded-xl p-4">
                            <p className="sqsft-caption font-bold uppercase tracking-wider mb-2" style={{ color: 'rgb(var(--color-deep-green))' }}>Implementation Approach</p>
                            <p className="sqsft-body text-sm leading-relaxed">{selectedIdea.trizFlow.implementation}</p>
                          </div>
                          <div className="sqsft-feature-card rounded-xl p-4">
                            <p className="sqsft-caption font-bold uppercase tracking-wider mb-2" style={{ color: 'rgb(var(--color-deep-green))' }}>Key Benefit</p>
                            <p className="sqsft-body text-sm leading-relaxed">{selectedIdea.trizFlow.benefit}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Business Case Tab */}
                {(window as any).activeIdeaTab === 'business' && (
                  <div className="space-y-8">
                    {/* Financial Overview */}
                    {selectedIdea.financialAnalysis && (
                      <div className="bg-gradient-to-br from-emerald-50 via-white to-green-50 rounded-2xl p-8 border border-emerald-200">
                        <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          Financial Projections
                        </h3>
                        
                        {/* Revenue Timeline - Visual Timeline */}
                        <div className="mb-8">
                          <div className="relative">
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-emerald-200 -translate-y-1/2"></div>
                            <div className="grid grid-cols-3 gap-4 relative">
                              <div className="text-center">
                                <div className="bg-white rounded-xl p-5 border-2 border-emerald-300 shadow-md relative">
                                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">Year 1</div>
                                  <p className="text-2xl font-bold text-emerald-700 mt-2">{selectedIdea.financialAnalysis.revenue_potential_year_1}</p>
                                  <p className="text-xs text-slate-600 mt-1">Initial Revenue</p>
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="bg-white rounded-xl p-5 border-2 border-emerald-400 shadow-md relative">
                                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">Year 3</div>
                                  <p className="text-2xl font-bold text-emerald-700 mt-2">{selectedIdea.financialAnalysis.revenue_potential_year_3}</p>
                                  <p className="text-xs text-slate-600 mt-1">Growth Phase</p>
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="bg-white rounded-xl p-5 border-2 border-emerald-500 shadow-md relative">
                                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-700 text-white text-xs font-bold px-3 py-1 rounded-full">Year 5</div>
                                  <p className="text-2xl font-bold text-emerald-700 mt-2">{selectedIdea.financialAnalysis.revenue_potential_year_5}</p>
                                  <p className="text-xs text-slate-600 mt-1">Mature Revenue</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
                            <div className="w-10 h-10 mx-auto mb-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                              <TrendingUp className="w-5 h-5 text-blue-600" />
                            </div>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">ROI</p>
                            <p className="font-bold text-lg text-slate-800 mt-1">{selectedIdea.financialAnalysis.roi_potential}</p>
                          </div>
                          <div className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
                            <div className="w-10 h-10 mx-auto mb-2 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center">
                              <Clock className="w-5 h-5 text-purple-600" />
                            </div>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Payback</p>
                            <p className="font-bold text-lg text-slate-800 mt-1">{selectedIdea.financialAnalysis.payback_period}</p>
                          </div>
                          <div className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
                            <div className="w-10 h-10 mx-auto mb-2 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg flex items-center justify-center">
                              <Target className="w-5 h-5 text-orange-600" />
                            </div>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Break-even</p>
                            <p className="font-bold text-lg text-slate-800 mt-1">{selectedIdea.financialAnalysis.break_even_timeline}</p>
                          </div>
                          <div className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
                            <div className="w-10 h-10 mx-auto mb-2 bg-gradient-to-br from-pink-100 to-pink-200 rounded-lg flex items-center justify-center">
                              <BarChart3 className="w-5 h-5 text-pink-600" />
                            </div>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">NPV</p>
                            <p className="font-bold text-lg text-slate-800 mt-1">{selectedIdea.financialAnalysis.net_present_value}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Market Opportunity */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="sqsft-feature-card p-6 border">
                        <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                          <Users className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                          <span>Market Opportunity</span>
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-slate-600">Market Size</p>
                            <p className="font-semibold">{selectedIdea.marketResearch.marketSize}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-600">Target Segment</p>
                            <p className="font-semibold">{selectedIdea.marketResearch.targetSegment}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-600">Competition</p>
                            <p className="font-semibold">{selectedIdea.marketResearch.competitorAnalysis}</p>
                          </div>
                        </div>
                      </div>

                      <div className="sqsft-feature-card p-6 border">
                        <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                          <TrendingUp className="w-5 h-5" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                          <span>Growth Potential</span>
                        </h4>
                        <div className="space-y-3">
                          {selectedIdea.futurePotential.fiveYear.slice(0, 3).map((item: string, idx: number) => (
                            <div key={idx} className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 rounded-full mt-1.5" style={{ backgroundColor: 'rgb(var(--color-royal-gold))' }}></div>
                              <p className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Revenue Model */}
                    <div className="sqsft-feature-card p-6 border">
                      <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                        <DollarSign className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                        <span>Revenue & Business Model</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-semibold text-slate-700 mb-2">Revenue Streams</p>
                          <div className="space-y-2">
                            {selectedIdea.commercialValue.revenueStreams.map((stream: string, idx: number) => (
                              <div key={idx} className="flex items-start space-x-2">
                                <ChevronRight className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-slate-700">{stream}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-700 mb-2">Value Generation</p>
                          <div className="space-y-2">
                            {selectedIdea.commercialValue.costSavings.map((saving: string, idx: number) => (
                              <div key={idx} className="flex items-start space-x-2">
                                <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-slate-700">{saving}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Implementation Tab */}
                {(window as any).activeIdeaTab === 'implementation' && (
                  <div className="space-y-6">
                    {/* Roadmap Timeline */}
                    <div className="sqsft-feature-card p-6 border-2" style={{ borderColor: 'rgba(239, 68, 68, 0.2)' }}>
                      <h3 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                        <Rocket className="w-5 h-5 text-red-600" />
                        <span>Implementation Roadmap</span>
                      </h3>
                      <div className="space-y-4">
                        {selectedIdea.implementationRoadmap.map((step: string, index: number) => (
                          <div key={index} className="flex items-start space-x-4">
                            <div className="relative">
                              <div className="w-10 h-10 sqsft-gradient-primary text-white rounded-full flex items-center justify-center font-bold shadow-lg">
                                {index + 1}
                              </div>
                              {index < selectedIdea.implementationRoadmap.length - 1 && (
                                <div className="absolute top-10 left-5 w-0.5 h-12 bg-gradient-to-b from-sage-green to-transparent"></div>
                              )}
                            </div>
                            <div className="flex-1 pb-8">
                              <p className="text-sm text-slate-700">{step}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Technical Architecture */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="sqsft-feature-card p-6 border">
                        <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                          <Cpu className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                          <span>Technical Architecture</span>
                        </h4>
                        <p className="text-sm text-slate-700 mb-4">{selectedIdea.technicalDeepDive.architecture}</p>
                        {selectedIdea.technicalDeepDive.integrationPoints && (
                          <div>
                            <p className="text-xs font-semibold text-slate-600 mb-2">Integration Points</p>
                            <div className="space-y-1">
                              {selectedIdea.technicalDeepDive.integrationPoints.map((point: string, idx: number) => (
                                <div key={idx} className="flex items-start space-x-2">
                                  <GitMerge className="w-3 h-3 text-slate-500 mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-slate-700">{point}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="sqsft-feature-card p-6 border">
                        <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                          <Users className="w-5 h-5" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                          <span>Resource Requirements</span>
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-slate-600 mb-1">Team Composition</p>
                            <p className="text-sm text-slate-700">{selectedIdea.resourceAllocation.team}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-600 mb-1">Budget Considerations</p>
                            <p className="text-sm text-slate-700">{selectedIdea.resourceAllocation.budgetNote}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Risk Assessment */}
                    {selectedIdea.marketResearch.riskFactors && (
                      <div className="sqsft-feature-card p-6 border">
                        <h4 className="sqsft-heading-4 mb-4 flex items-center space-x-2">
                          <Shield className="w-5 h-5 text-orange-600" />
                          <span>Risk Assessment</span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedIdea.marketResearch.riskFactors.map((risk: string, idx: number) => (
                            <div key={idx} className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                              <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-slate-700">{risk}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default IdeaEvaluationPhase;